/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import { Component, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BasicAlertComponent } from '../alerts/basic-alert.component';
import { Workflow, WorkflowService } from '../../services/workflow.service';
import { ProductPlanTier, UserService } from '../../services/user.service';
import {
  AccessListConfig,
  AccessListService,
} from '../../services/access-list.service';
import { XdaStringsService } from '../../services/xda-strings.service';
import { EmailService } from '../../services/email.service';
import { XdaTranslate } from '../../pipes/xda-translate.pipe';
import { Constants } from '../../../common/constants';
import {
  AddressType,
  EditAddressComponent,
} from '../modals/edit-address.component';

@Component({
  selector: 'configure-services-features',
  templateUrl: './configure-services-features.component.html',
  styleUrls: ['./configure-services-features.component.less'],
  standalone: false,
})
export class ConfigureServicesFeaturesComponent implements OnInit {
  @ViewChild('f', { static: false }) form: NgForm;
  selectedConfig = 'workflows';
  isAdvancedPlan = false;
  allowSave = false;
  accessListId = '';
  hideMergeWorkflows = false;
  preconfiguredWorkflows: Workflow[] = [];
  combinedWorkflows: Workflow[] = [];
  storedConfig: AccessListConfig;
  config: {
    email: boolean;
    enableTenant: boolean;
    enableAutoWorkflows: boolean;
    enableDomainRestrictions: boolean;
    allowedDomains: string[];
    repos: Record<string, boolean>;
    workflows: Record<string, boolean>;
  } = {
    email: true,
    enableTenant: false,
    enableAutoWorkflows: true,
    enableDomainRestrictions: false,
    allowedDomains: [],
    repos: {},
    workflows: {},
  };
  configToSave: AccessListConfig = {
    hiddenWorkflows: [],
    disableCloudConnectors: [],
    disableEncryptedEmail: false,
    disableAutoWorkflows: false,
    enableTenant: false,
    tenantId: '',
    enableDomainRestrictions: false,
    allowedDomains: [],
  };
  tenantId: string;
  cloudRepos = Constants.middlewareDestinations;
  tenantToolTip = '';
  tenantInput: any = {
    format: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
    validation: /^[{]?[0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}[}]?$/,
    validationError: 'SDE_FORMATTING_ERROR_ID',
    maxlength: 36,
    maxlengthError: '',
  };
  enterDomain = '';
  errorDomain = '';
  maxDomains = 10;
  maxDomainsError: string;
  activeTab = 'SDE_WORKFLOWS';

  private xdaTranslatePipe: XdaTranslate;
  constructor(
    public activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private workflowService: WorkflowService,
    private userService: UserService,
    private accessListService: AccessListService,
    private xdaStringsService: XdaStringsService,
    private emailService: EmailService,
  ) {
    this.xdaTranslatePipe = new XdaTranslate(this.xdaStringsService);
  }

  ngOnInit(): void {
    this.tenantToolTip = this.xdaTranslatePipe.transform(
      'SDE_MICROSOFT_365_SHAREPOINT',
    );
    this.tenantInput.maxlengthError = this.xdaTranslatePipe.transform(
      'SDE_MAX_CHARACTER_LENGTH',
      this.tenantInput.maxlength,
    );
    this.maxDomainsError = this.xdaTranslatePipe.transform(
      'SDE_MAXIMUM_FMTSTR_DOMAINS',
      [this.maxDomains.toString()],
    );

    // get plan type
    this.isAdvancedPlan =
      (this.userService?.user?.acquisition?.productPlanTier || 0) >=
      ProductPlanTier.Advanced;

    // get access list id
    this.accessListId = this.userService?.user?.accessListId || '';

    // get stored config
    this.accessListService
      .getAccessConfigConfig(this.accessListId)
      .then((resp) => {
        if (resp) {
          this.storedConfig = resp;
        }

        // get workflows
        this.getWorkflows(this.hideMergeWorkflows);

        // add cloud repos to config - append region to value to make unique for region specific connectors.
        this.config.repos = {};
        this.cloudRepos.forEach((r) => {
          // if stored config is not null set the repos enablement values from the stored config
          if (this.storedConfig) {
            this.config.repos[r.value + r.region] =
              !this.storedConfig.disableCloudConnectors.includes(
                r.value + r.region,
              );
          } else {
            // else set all repo values to true
            this.config.repos[r.value + r.region] = true;
          }
        });

        // add email and tenant to config
        if (this.storedConfig) {
          // if stored config is not null set the email enablement value from the stored config
          this.config.email = !this.storedConfig.disableEncryptedEmail;
          this.config.enableTenant = this.storedConfig.enableTenant
            ? this.storedConfig.enableTenant
            : false;
          this.tenantId = this.storedConfig.tenantId;
          this.config.enableAutoWorkflows =
            this.storedConfig.disableAutoWorkflows !== undefined
              ? !this.storedConfig.disableAutoWorkflows
              : true;
          this.config.enableDomainRestrictions =
            this.storedConfig.enableDomainRestrictions !== undefined
              ? this.storedConfig.enableDomainRestrictions
              : false;
          this.config.allowedDomains =
            this.storedConfig.allowedDomains !== undefined
              ? this.storedConfig.allowedDomains
              : [];
        } else {
          // else set email value to true, tenant to false
          this.config.email = true;
          this.config.enableTenant = false;
          this.config.enableAutoWorkflows = true;
          this.config.enableDomainRestrictions = false;
        }
      })
      .catch((resp) => {
        const modalRef = this.modalService.open(BasicAlertComponent);
        modalRef.componentInstance.title = 'SDE_CANNOT_LOAD_WORKFLOW';
        modalRef.componentInstance.body = 'SDE_PLEASE_TRY_AGAIN1';
      });
  }

  getWorkflows(hideMergeWorkflows = false) {
    this.workflowService?.getWorkflows()?.subscribe(
      (resp) => {
        this.allowSave = true;
        this.workflowService.workflows = resp;
        this.combinedWorkflows = [];
        this.config.workflows = {};
        this.workflowService.workflows.forEach((w) => {
          if (
            !w.isSingleService &&
            w.type === 0 &&
            !(hideMergeWorkflows && w.input.maxConcatenatedDocument >= 1)
          ) {
            this.combinedWorkflows.push(w);
          }
          // populate config with workflows enablement values
          if (
            w.type === 0 &&
            !(hideMergeWorkflows && w.input.maxConcatenatedDocument >= 1)
          ) {
            // if stored config is not null set the workflows enablement values from the stored config
            if (this.storedConfig && this.storedConfig.hiddenWorkflows) {
              this.config.workflows[w.id || ''] =
                !this.storedConfig.hiddenWorkflows.includes(w.id || '');
            } else {
              // else set all workflow values to true
              this.config.workflows[w.id || ''] = true;
            }
          }
        });
        this.combinedWorkflows.sort((a, b) => a.name.localeCompare(b.name));
        this.preconfiguredWorkflows = this.workflowService.workflows
          .filter(
            (x) =>
              x.type == 0 &&
              x.isSingleService &&
              x.name !== 'SDE_CATEGORIZE' &&
              !(hideMergeWorkflows && x.input.maxConcatenatedDocument >= 1),
          )
          .sort((a, b) => {
            const aTranslated = this.xdaStringsService.strings[a.name] || a.name;
            const bTranslated = this.xdaStringsService.strings[b.name] || b.name;
            return aTranslated.localeCompare(bTranslated);
          });
      },
      () => {
        const modalRef = this.modalService.open(BasicAlertComponent);
        modalRef.componentInstance.title = 'SDE_ERROR_GETTING_WORKFLOWS';
      },
    );
  }

  selectConfig(newValue: string, f: NgForm) {
    let invalid = false;

    // check Security required fields
    if (newValue !== 'security') {
      if (
        this.canEnableTenantId() &&
        this.config.enableTenant &&
        !this.tenantId
      ) {
        f.control.controls['tenantId'].markAsDirty();
        f.control.controls['tenantId'].markAsTouched();
        invalid = true;
      }
      if (this.config.enableDomainRestrictions && !this.isDomains()) {
        f.control.controls['enterDomain'].markAsDirty();
        f.control.controls['enterDomain'].markAsTouched();
        invalid = true;
      }
    }

    if (!invalid && !f.invalid) {
      this.selectedConfig = newValue;
    }
  }

  private isDomains() {
    if (this.enterDomain != '' && !this.addDomain()) {
      return false;
    }
    if (!this.config.allowedDomains.length) {
      this.errorDomain = '';
      setTimeout(() => {
        this.errorDomain = 'SDE_MIN1_DOMAIN_REQUIRED';
      });
      this.form.form?.get('enterDomain')?.setErrors({ customerror: true });
      return false;
    }
    return true;
  }

  async saveConfig(f: NgForm) {
    if (this.canEnableTenantId() && f.invalid) {
      return;
    }

    if (this.config.enableDomainRestrictions && !this.isDomains()) {
      return;
    }

    for (const [key, value] of Object.entries(this.config.workflows)) {
      if (value === false) {
        this.configToSave.hiddenWorkflows.push(key);
      }
    }

    for (const [key, value] of Object.entries(this.config.repos)) {
      if (value === false) {
        this.configToSave.disableCloudConnectors.push(key);
      }
    }

    this.configToSave.allowedDomains = this.config.allowedDomains.filter(
      (value, index) => this.config.allowedDomains.indexOf(value) === index,
    ); // remove duplicates
    this.configToSave.enableDomainRestrictions =
      this.config.enableDomainRestrictions;
    this.configToSave.disableAutoWorkflows = !this.config.enableAutoWorkflows;
    this.configToSave.disableEncryptedEmail = !this.config.email;
    this.configToSave.enableTenant = this.config.enableTenant;
    this.configToSave.tenantId = this.tenantId;

    this.accessListService
      .insertAccessConfigConfig(this.accessListId, this.configToSave)
      .then(() => {
        this.activeModal.close();
      })
      .catch((error) => {
        console.log(error);
        const modalRef = this.modalService.open(BasicAlertComponent);
        modalRef.componentInstance.title = 'SDE_CANNOT_SAVE_WORKFLOW';
        modalRef.componentInstance.body = 'SDE_PLEASE_TRY_AGAIN1';
      });
  }

  onToggle(event: boolean, item: string, type: string) {
    type ConfigTypeKey = keyof typeof this.config;
    if (type) {
      const nestedConfig = this.config[type as ConfigTypeKey];
      if (
        nestedConfig &&
        typeof nestedConfig === 'object' &&
        !Array.isArray(nestedConfig)
      ) {
        nestedConfig[item] = !nestedConfig[item];
      }
    } else {
      const key = item as Exclude<
        ConfigTypeKey,
        'allowedDomains' | 'repos' | 'workflows'
      >;
      this.config[key] = !this.config[key];
    }
  }

  onToggleAutoWorkflows(event: boolean, item: string, type: string) {
    this.onToggle(event, item, type);

    if (!event) {
      // If disable then display confirmation modal
      const modalRef = this.modalService.open(BasicAlertComponent);
      modalRef.componentInstance.title = 'SDE_ACTION_WILL_DISABLE';
      modalRef.componentInstance.buttonText = 'SDE_CANCEL';
      modalRef.componentInstance.okButtonText = 'SDE_CONTINUE';
      modalRef.result.then((data) => {
        if (data !== 'Ok') {
          // cancelled, reset the toggle
          this.config.enableAutoWorkflows = !this.config.enableAutoWorkflows;
        }
      });
    }
  }

  canEnableTenantId() {
    let enabled = false;

    // enable tenantId only if OneDrive or SharePoint feature is enabled.
    for (const [key, value] of Object.entries(this.config?.repos)) {
      if (key === 'GraphOneDrive' || key === 'GraphSharePoint') {
        enabled ||= Boolean(value);
      }
    }
    return enabled;
  }

  addDomain(): boolean {
    this.errorDomain = '';
    const validDomains = this.validateEntries(
      this.enterDomain.toLowerCase(),
      this.isValidDomain,
    );
    if (validDomains) {
      if (
        validDomains.length + this.config.allowedDomains.length <=
        this.maxDomains
      ) {
        validDomains.forEach((entry) => {
          // add if it doesn't exist already
          if (!this.config.allowedDomains.find((x) => x == entry)) {
            this.config.allowedDomains.push(entry);
          }
        });

        this.enterDomain = ''; // clear the entry field
        return true;
      } else {
        setTimeout(() => {
          this.errorDomain = this.maxDomainsError;
        });
        this.form.form.get('enterDomain')?.setErrors({ customerror: true });
        return false;
      }
    } else {
      setTimeout(() => {
        this.errorDomain = 'SDE_ONE_MORE_DOMAINS';
      });
      this.form.form.get('enterDomain')?.setErrors({ customerror: true });
      return false;
    }
  }

  removeDomain(index: number) {
    this.config.allowedDomains.splice(index, 1);

    if (this.errorDomain == this.maxDomainsError) {
      // clear the error
      setTimeout(() => {
        this.errorDomain = '';
      });
      const ctrl = this.form.form.get('enterDomain');
      if (ctrl) {
        ctrl.setErrors({});
        ctrl.markAsPristine();
        ctrl.updateValueAndValidity();
      }
    }
  }

  editDomain(index: number) {
    const modalRef = this.modalService.open(EditAddressComponent, {
      windowClass: 'xrx-modal modal-md',
    });
    modalRef.componentInstance.type = AddressType.domain;
    modalRef.componentInstance.address = this.config.allowedDomains[index];
    modalRef.componentInstance.pattern = this.emailService.domainRegex;
    modalRef.result.then((value) => {
      this.config.allowedDomains[index] = value.toLowerCase();
    });
  }

  // Generic function call to validate a string containing one or more email or domain entries.
  //  entriesStr:  the string with one or more comma/semicolon separated values
  //  validateFunction: function to validate each entry which returns true/false
  validateEntries(
    entriesStr: string,
    validateFunction: (entry: string) => boolean,
  ) {
    entriesStr = entriesStr.trim();

    if (entriesStr.endsWith(';') || entriesStr.endsWith(',')) {
      entriesStr = entriesStr.slice(0, -1);
    }

    // remove any case-insensitive duplicates
    const entries: string[] = [];
    entriesStr.split(/[ ,;]+/).forEach(function (entry) {
      if (
        entry !== '' &&
        !(entries.includes(entry) || entries.includes(entry.toLowerCase()))
      )
        entries.push(entry.trim());
    });

    // check for any invalid entry
    const invalidIndex = entries.findIndex((entry) => {
      if (entry !== '') {
        return !validateFunction.bind(this)(entry); // note: bind(this) makes 'this' accessible inside the validateFunction.
      }
      return false;
    });

    // if all entries are valid return the array
    return invalidIndex == -1 ? entries : null;
  }

  isValidDomain(domain: string): boolean {
    return this.emailService.isValidDomain(domain);
  }
}
